import _, { Dictionary } from 'lodash';
import Vue from 'vue';
import {mapActions, mapGetters, mapState} from 'vuex';
import {maxLength, required, decimal} from 'vuelidate/lib/validators';
import EventHub from '../../lib/eventHub';
import {AxiosResponse} from 'axios';
import ClubService from '../../lib/services/clubService';
import BioConfigService from '../../lib/services/bioConfigService';
import TagsService from '../../lib/services/tagsService';
import TagsMuleService from '../../lib/services/tagsMuleService';
import ServicesService from '../../lib/services/servicesService';
import ServicesMuleService from '../../lib/services/servicesMuleService';
import {AlertNotificationModuleConstant, BioProfileModuleConstant, BioServicesConstant, EmployeeProfileModuleConstant, EventConstant, RoleConfigs, UrlModuleConstant} from '../../lib/constants';
import JobDetails from './edit-job-details.html';
import BioAdmin from 'types/ba/bioAdmin';

interface IComponentData {
	bioProfileServiceLoading: boolean;
	employeeId: string;
	countries: BioAdmin.ICountry[];
	clubs: BioAdmin.IProfileClub[];
	services: BioAdmin.ITag[];
	tagGroups: BioAdmin.ITagGroup[];
	servicesTags: object;
	specialtiesTags: object;
	specialties: BioAdmin.ITagGroup[];
	offeringDesc: string;
	roles: string[];
	biosConfigResponse: BioAdmin.IBiosConfigResponse;
	roleFeatures: string[];
	form: BioAdmin.IJobDetailsFormValidator;
	useSpringApi: boolean;
}

interface JQuery {
    multiselect(): void;
}

const clubService: BioAdmin.IClubService = new ClubService();
const bioConfigService: BioAdmin.IBioConfigService = new BioConfigService();
const tagsService: BioAdmin.ITagsService = new TagsService();
const tagsMuleService: BioAdmin.ITagsService = new TagsMuleService();
const servicesService: BioAdmin.IServicesService = new ServicesService();
const servicesMuleService: BioAdmin.IServicesService = new ServicesMuleService();

export default Vue.component('jobDetails', {
	template: JobDetails,
	data(): IComponentData {
		return {
			bioProfileServiceLoading: true,
			employeeId: '',
			countries: [],
			clubs: [],
			services: [],
			tagGroups: [],
			servicesTags: {},
			specialtiesTags: {},
			specialties: [],
			offeringDesc: '',
			roles: [],
			biosConfigResponse:  {
				rolesFeatures: [
					{
						roleId: 1,
						roleName : 'Personal Trainer',
						features: [
							'contact',
							'quote'
						]
					}],
				features:
				{
					quote: {
						maxLength: '50',
					},
					contact: {}
				}
			},
			roleFeatures: [],
			form: {
				services: [],
				specialties: [],
				clubs: [],
			},
			useSpringApi: false,
		};
	},
	computed: {
		...mapState(BioProfileModuleConstant.name, [
			'bioProfile',
		]),
		...mapGetters(BioProfileModuleConstant.name, {
			getClubs: BioProfileModuleConstant.getter.getClubs,
			getServices: BioProfileModuleConstant.getter.getServices,
			getSpecialties: BioProfileModuleConstant.getter.getSpecialties,
			getSchedule: BioProfileModuleConstant.getter.getSchedule,
		}),
		...mapGetters(UrlModuleConstant.name, {
			getUrlParameter: UrlModuleConstant.getter.getUrlParameter,
		}),
	},
	methods: {
		...mapActions(AlertNotificationModuleConstant.name, {
			synchronizeAlertNotificationStore: AlertNotificationModuleConstant.action.synchronize,
		}),
		...mapActions(EmployeeProfileModuleConstant.name, {
			synchronizeEmployeeProfileStore: EmployeeProfileModuleConstant.action.synchronize,
		}),
		...mapActions(UrlModuleConstant.name, {
			synchronizeUrlStore: UrlModuleConstant.action.synchronize,
		}),
		...mapActions(BioProfileModuleConstant.name, {
			getProfile: BioProfileModuleConstant.action.getProfile,
			modifyProfileClubs: BioProfileModuleConstant.action.modifyProfileClubs,
			removeProfileClub: BioProfileModuleConstant.action.removeProfileClub,
			removeProfileService: BioProfileModuleConstant.action.removeProfileService,
			removeProfileSpecialty: BioProfileModuleConstant.action.removeProfileSpecialty,
			modifyProfileServices: BioProfileModuleConstant.action.modifyProfileServices,
			modifyProfileSpecialties: BioProfileModuleConstant.action.modifyProfileSpecialties,
			modifySchedule: BioProfileModuleConstant.action.modifySchedule,
		}),
		getAllClubs() {
			return clubService.getClubLocations()
				.then((response: AxiosResponse<BioAdmin.IClubsResponse>) => {
					this.setAllClubs(response.data);
				});
		},
		setAllClubs(clubs: any): void {
			const countries: BioAdmin.ICountry[] = [];
			const countryClubs: Dictionary<BioAdmin.IClub[]> = _.groupBy(clubs, 'country');
			_.forEach(countryClubs, (cValue, cKey) => {
				const stateProvinces: BioAdmin.IStateProvince[] = [];
				const stateClubs: Dictionary<BioAdmin.IClub[]> = _.groupBy(cValue, 'state');
				_.forEach(stateClubs, (sValue, sKey) => {
					stateProvinces.push({
						name: sKey,
						clubs: _.orderBy(sValue, 'clubTitle'),
					});
				});
				countries.push({
					name: cKey,
					stateProvinces: _.orderBy(stateProvinces, 'name'),
				});
			});
			this.countries = countries;
		},
		clubListModified(club: BioAdmin.IClub, event: Event): void {
			const data: BioAdmin.IProfileClub = {
				clubId: club.mmsClubId,
				name: club.name,
				selected: (event.currentTarget as HTMLInputElement).checked,
			};
			this.modifyProfileClubs(data);
        },
		removeClub(index: number): void {
			this.removeProfileClub(index);
		},
		isClubInProfile(clubId: number): boolean {
			return _.some(this.bioProfile.clubs, (club: BioAdmin.IProfileClub) => {
				return club.clubId === clubId;
			});
		},
		loadProfileClubs(): void {
			this.clubs = this.getClubs;
		},
		loadProfileServices(): void {
			this.services = this.getServices;
		},
		getRoleConfig() {
			return bioConfigService.getRoleConfig()
				.then((response: AxiosResponse<BioAdmin.IBiosConfigResponse>) => {
					this.biosConfigResponse = response.data;
				}).catch((error) => {
					// use config constant on localhost
					this.biosConfigResponse = RoleConfigs;
				});
		},
		getTagGroupsAndData() {
			if (this.useSpringApi) {
				return tagsService.getTagGroupsAndData()
					.then((response: AxiosResponse<BioAdmin.ITagGroup[]>) => {
						this.tagGroups = response.data;
						this.setTags(BioServicesConstant.specialtiesName);
					});
			} else {
				return tagsMuleService.getTagGroupsAndData()
				.then((response: AxiosResponse<BioAdmin.ITagGroup[]>) => {
					this.tagGroups = response.data;
					this.setTags(BioServicesConstant.specialtiesName);
				});
			}
		},
		// set tags for dropdown lists
		setTags(name: string) {
			if (name === BioServicesConstant.specialtiesName) {
				const group: BioAdmin.ITagGroup | undefined = _.find(this.tagGroups, (tg: BioAdmin.ITagGroup) => tg.name === name);
				if (group) {
					this.specialtiesTags = _.orderBy(group.tags, 'name');
				}
			}
		},
		isSpecialtyInProfile(name: string): boolean {
			return _.some(this.specialties, (tag: BioAdmin.ITag) => {
				return tag.name === name;
			});
		},
		getServicesList(roleId: number) {
			if (this.useSpringApi) {
				return servicesService.getServices(roleId)
					.then((response: AxiosResponse<BioAdmin.IServiceTag[]>) => {
						this.servicesTags = _.orderBy(response.data, 'name');
					});
			} else {
				return servicesMuleService.getServices(roleId)
					.then((response: AxiosResponse<BioAdmin.IServiceTag[]>) => {
						this.servicesTags = _.orderBy(response.data, 'name');
					});
			}
		},
		isServiceInProfile(name: string): boolean {
			return _.some(this.bioProfile.services, (service: BioAdmin.IServiceTag) => {
				return service.name === name;
			});
		},
		serviceListModified(tag: BioAdmin.ITag): void {
			this.modifyProfileServices(tag);
		},
		removeService(index: number): void {
			this.removeProfileService(index);
		},
		removeSelectedService(index: number): void {
			this.services.splice(index, 1);
		},
		specialtyListModified(tag: BioAdmin.ITag): void {
			this.modifyProfileSpecialties(tag);
		},
		removeSpecialty(index: number): void {
			this.removeProfileSpecialty(index);
		},
		removeSelectedSpeciality(index: number): void {
			this.services.splice(index, 1);
		},
		hasFeature(feature: string): boolean {
			return this.roleFeatures.includes(feature);
		},
		hasRole(role: string): boolean {
			return this.roles.includes(role);
		},
		setFeaturesByRole() {
			this.roles = [];
			this.roleFeatures = [];
			// @ts-ignore
			_.forEach(this.bioProfile.roles, (profileRole: BioAdmin.IRole) => {
				const validRole: any = _.find(this.biosConfigResponse.rolesFeatures, (rc) => {
					return profileRole.name === rc.roleName;
				});
				this.roles.push(validRole.roleName);
				_.forEach(validRole.features, (feature) => {
					if (!this.roleFeatures.includes(feature)) {
						this.roleFeatures.push(feature);
					}
				});
			});
		},
		bindMultiSelects(): void {
			// @ts-ignore
			$('.multiselect').multiselect();
		},
		checkSchedule(dayIndex: number, timeIndex: number, clubId: any): boolean {
			const profileClub: BioAdmin.IProfileClub = _.find(this.bioProfile.clubs, (club: any) => {
				return club.clubId === clubId;
			});
			if (profileClub.schedule) {
				return profileClub.schedule[dayIndex].items[timeIndex].active;
			} else {
				return false;
			}
		},
		updateSchedule(dayIndex: number, timeIndex: number, clubId: number, value: any): void {
			const updateObj: any = {
				dayIndex: dayIndex,
				timeIndex: timeIndex,
				clubId: clubId,
				value: value,
			};
			this.modifySchedule(updateObj);
		},
	},
	watch: {
		bioProfile() {
			this.clubs = this.getClubs;
			this.services = this.getServices;
			this.specialties = this.getSpecialties;
			this.form = {
				services: this.services,
				specialties: this.specialties,
				schedule: this.getSchedule,
				clubs: this.getClubs,
			};
		},
		roleFeatures() {
			let str: string = 'You may add up to ';
			if (this.hasFeature('services')) {
				str = str + 'three services'
				if (this.hasFeature('specialties')) {
					str = str + ' and ';
				}
			}
			if (this.hasFeature('specialties')) {
				str = str + 'three specialties'
			}
			this.offeringDesc = str + '.';
		}
	},
	created(): void {
		EventHub.$on(EventConstant.BioProfileServiceLoading, (loading: boolean) => {
			this.bioProfileServiceLoading = loading;
		});

		Promise.all([this.synchronizeAlertNotificationStore(), this.synchronizeEmployeeProfileStore(), this.synchronizeUrlStore(), , this.getRoleConfig()]).then(() => {
			const featureFlags = window.lt.features;
			if(featureFlags) {
				const feature = featureFlags[featureFlags.findIndex( (x: any) => x.featureId === 'biosSpringApi')];
				if(feature && feature.enabled)
				this.useSpringApi = true;
			}
			this.getProfile().then(() => {
				// get tag groups
				this.getTagGroupsAndData();
				this.setFeaturesByRole();
				// hard-coded roleId of 2 for services call
				this.getServicesList(2);
				// enable multiselects
				setTimeout(this.bindMultiSelects, 1000);
			});
		});
	},
	validations() {
		// build dynamic validation object based on role access for required fields
		const form: any = {};
		if (this.hasFeature('services')) {
			form['services'] = {
				required,
				maxLength: maxLength(5),
			};
		}
		if (this.hasFeature('specialties')) {
			form['specialties'] = {
				required,
				maxLength: maxLength(5),
			};
		}
		return {
			form,
		};
	},
});
